import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { UnitToggle } from '../common/UnitToggle';
import { WeightInput } from '../common/WeightInput';
import { useToast } from '../common/Toast';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { useLanguage } from '../../features/language/useLanguage';
import { formatCurrency } from '../../utils/format';
import { roastedBulkService } from '../../services/inventory/roastedBulkService';
import { previewRoastedBulkFifoDraw } from './roastedBulkFifoPreview';
import type { RoastedBulkAdjustmentReason, RoastedBulkGroup } from '../../types/inventory';

interface RoastedBulkGroupAdjustModalProps {
  group: RoastedBulkGroup | null;
  isOpen: boolean;
  currencySymbol: string;
  onClose: () => void;
  onAdjusted: () => void;
}

const REASONS: RoastedBulkAdjustmentReason[] = ['waste', 'sample', 'internal', 'correction'];

// Field chrome is lifted verbatim from InventoryHistoryDialog, the per-lot
// sibling of this dialog. The two are the same task at different scope, so they
// must not look like they came from different products.
const FIELD_LABEL = 'block text-xs font-semibold uppercase tracking-wider text-slate-500';
// Every label sits in a row of the same height, whether or not it carries a
// control. UnitToggle is 2rem tall, so without this the quantity label pushes
// its input below the neighbouring one and the two fields stop lining up.
const FIELD_LABEL_ROW = 'mb-2 flex min-h-[2rem] items-center justify-between gap-3';
const FIELD_INPUT =
  'block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all ' +
  'focus:border-primary-500 focus:ring-primary-500 placeholder:text-slate-400';

export const RoastedBulkGroupAdjustModal: React.FC<RoastedBulkGroupAdjustModalProps> = ({
  group,
  isOpen,
  currencySymbol,
  onClose,
  onAdjusted,
}) => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const { unit, setUnit } = useWeightUnit('kg');
  const [quantityKg, setQuantityKg] = useState<number | undefined>(undefined);
  const [reason, setReason] = useState<RoastedBulkAdjustmentReason | ''>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupItemId = group?.itemId;
  const groupRoastType = group?.roastType;

  // A quantity carried over from the previous group would preview against lots
  // the user never looked at.
  useEffect(() => {
    if (isOpen) {
      setQuantityKg(undefined);
      setReason('');
      setNotes('');
      setError('');
    }
  }, [isOpen, groupItemId, groupRoastType]);

  const available = group?.totalStock ?? 0;
  const hasValidQuantity = typeof quantityKg === 'number' && Number.isFinite(quantityKg) && quantityKg > 0;
  const exceedsAvailable = hasValidQuantity && quantityKg > available + 1e-9;
  const groupItems = group?.items;

  const preview = useMemo(() => {
    if (!groupItems || !hasValidQuantity || exceedsAvailable) return [];
    return previewRoastedBulkFifoDraw(groupItems, quantityKg as number);
  }, [groupItems, quantityKg, hasValidQuantity, exceedsAvailable]);

  const previewTotalCost = useMemo(
    () => preview.reduce((sum, row) => sum + row.cost, 0),
    [preview],
  );

  // Weights follow the toggle; money never does. Coffee is costed per kg
  // whatever unit the operator is typing in.
  const formatWeight = (valueKg: number) => {
    if (!Number.isFinite(valueKg)) return '-';
    return unit === 'kg' ? valueKg.toFixed(3) : String(Math.round(valueKg * 1000));
  };
  const unitLabel = unit === 'kg' ? 'kg' : 'g';

  const canSubmit =
    !!group && hasValidQuantity && !exceedsAvailable && !!reason && notes.trim() !== '' && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!group || !hasValidQuantity) {
      setError(t('inventory.roastedBulk.adjust.errors.quantityRequired'));
      return;
    }
    if (exceedsAvailable) {
      setError(
        t('inventory.roastedBulk.adjust.errors.quantityTooLarge', {
          available: `${formatWeight(available)} ${unitLabel}`,
        }),
      );
      return;
    }
    if (!reason) {
      setError(t('inventory.roastedBulk.adjust.errors.reasonRequired'));
      return;
    }
    if (notes.trim() === '') {
      setError(t('inventory.roastedBulk.adjust.errors.notesRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      // The response is what actually happened; the preview above was advisory.
      const result = await roastedBulkService.adjustRoastedBulkGroupStock({
        groupId: group.itemId,
        roastType: group.roastType,
        quantityKg: quantityKg as number,
        reason,
        notes: notes.trim(),
      });
      showToast(
        t('inventory.roastedBulk.adjust.success', {
          quantity: `${formatWeight(result.totalQuantityKg)} ${unitLabel}`,
          cost: formatCurrency(result.totalCost, currencySymbol),
          item: group.itemName,
        }),
        'success',
      );
      onAdjusted();
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        t('inventory.roastedBulk.adjust.errors.failed');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!group) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('inventory.roastedBulk.adjust.title')}
      maxWidth="2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-test-id="rb-group-adjust-form">
        {/* What you are about to change, and what it leaves behind. */}
        <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-5 py-4">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-slate-800">{group.itemName}</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              {group.roastType || t('inventory.roastedBulk.unknownRoastType')}
            </span>
          </div>
          <dl className="mt-3 flex flex-wrap gap-x-10 gap-y-2">
            <div>
              <dt className={FIELD_LABEL}>{t('inventory.roastedBulk.adjust.availableLabel')}</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-700">
                {formatWeight(available)} {unitLabel}
              </dd>
            </div>
            {hasValidQuantity && !exceedsAvailable && (
              <div>
                <dt className={FIELD_LABEL}>{t('inventory.roastedBulk.adjust.afterLabel')}</dt>
                <dd className="mt-0.5 text-sm font-semibold tabular-nums text-primary-700">
                  {formatWeight(available - (quantityKg as number))} {unitLabel}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className={FIELD_LABEL_ROW}>
              <label htmlFor="rb-adjust-qty" className={FIELD_LABEL}>
                {t('inventory.roastedBulk.adjust.quantityLabel')}
              </label>
              <UnitToggle unit={unit} onToggle={setUnit} className="shrink-0" />
            </div>
            <WeightInput
              id="rb-adjust-qty"
              data-test-id="rb-group-adjust-quantity"
              value={quantityKg}
              onChange={setQuantityKg}
              unit={unit}
              className={FIELD_INPUT}
              placeholder={
                unit === 'kg'
                  ? t('inventory.history.placeholderKg')
                  : t('inventory.history.placeholderGram')
              }
            />
            {exceedsAvailable && (
              <p className="mt-1.5 text-xs text-red-600" data-test-id="rb-group-adjust-exceeds">
                {t('inventory.roastedBulk.adjust.errors.quantityTooLarge', {
                  available: `${formatWeight(available)} ${unitLabel}`,
                })}
              </p>
            )}
          </div>

          <div className="md:col-span-7">
            <div className={FIELD_LABEL_ROW}>
              <label htmlFor="rb-adjust-reason" className={FIELD_LABEL}>
                {t('inventory.roastedBulk.adjust.reasonLabel')}
              </label>
            </div>
            <select
              id="rb-adjust-reason"
              data-test-id="rb-group-adjust-reason"
              value={reason}
              onChange={event => setReason(event.target.value as RoastedBulkAdjustmentReason | '')}
              className={FIELD_INPUT}
            >
              <option value="">{t('inventory.roastedBulk.adjust.reasonPlaceholder')}</option>
              {REASONS.map(value => (
                <option key={value} value={value}>
                  {t(`inventory.roastedBulk.adjust.reasons.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-12">
            <div className={FIELD_LABEL_ROW}>
              <label htmlFor="rb-adjust-notes" className={FIELD_LABEL}>
                {t('inventory.roastedBulk.adjust.notesLabel')}
              </label>
            </div>
            <input
              id="rb-adjust-notes"
              type="text"
              data-test-id="rb-group-adjust-notes"
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder={t('inventory.roastedBulk.adjust.notesPlaceholder')}
              className={FIELD_INPUT}
            />
          </div>
        </div>

        {/* The lots are chosen for the user, so showing the draw is the whole
            basis for trusting it. This is the one place the dialog raises its
            voice — hence the only accent rail. */}
        <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2">
            <div className="h-5 w-1 rounded-full bg-primary-600" />
            <h4 className="text-base font-semibold text-slate-800">
              {t('inventory.roastedBulk.adjust.previewTitle')}
            </h4>
          </div>
          <p className="mb-4 pl-3 text-xs text-slate-500">
            {t('inventory.roastedBulk.adjust.subtitle')}
          </p>

          {preview.length === 0 ? (
            <p className="py-3 text-sm text-slate-400">
              {t('inventory.roastedBulk.adjust.previewEmpty')}
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                      {t('inventory.roastedBulk.adjust.columns.workOrder')}
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                      {t('inventory.roastedBulk.adjust.columns.quantity')} ({unitLabel})
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                      {t('inventory.roastedBulk.adjust.columns.unitCost')}
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                      {t('inventory.roastedBulk.adjust.columns.cost')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.map(row => {
                    const survives = Math.max(row.lotRemainingBefore - row.quantityKg, 0);
                    const survivesPct =
                      row.lotRemainingBefore > 0 ? (survives / row.lotRemainingBefore) * 100 : 0;
                    const emptied = survivesPct < 0.05;
                    return (
                      <tr key={row.inventoryItemId}>
                        <td className="px-4 py-3 align-top">
                          <div className="text-sm font-medium text-slate-800">{row.workOrderNumber}</div>
                          {/* Same device as the lot detail page: the fill is
                              what is left, so an emptied lot reads as empty. */}
                          <div className="mt-1.5 h-1.5 w-full max-w-[9rem] rounded-full bg-gray-200" aria-hidden="true">
                            <div
                              className="h-1.5 rounded-full bg-primary-600 transition-[width] duration-200 motion-reduce:transition-none"
                              style={{ width: `${survivesPct}%` }}
                            />
                          </div>
                          {emptied && (
                            <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                              {t('inventory.roastedBulk.adjust.lotEmptied')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-sm tabular-nums text-slate-600">
                          {formatWeight(row.quantityKg)}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-sm tabular-nums text-slate-500">
                          {formatCurrency(row.unitCost, currencySymbol)}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-sm tabular-nums text-slate-600">
                          {formatCurrency(row.cost, currencySymbol)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-slate-200">
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500"
                    >
                      {t('inventory.roastedBulk.adjust.previewTotal')}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-slate-800">
                      {formatCurrency(previewTotalCost, currencySymbol)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {error && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            data-test-id="rb-group-adjust-error"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:bg-slate-50 active:scale-[0.98]"
          >
            {t('inventory.roastedBulk.adjust.cancel')}
          </button>
          <button
            type="submit"
            data-test-id="rb-group-adjust-submit"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-primary-200 transition-all hover:bg-primary-700 hover:shadow-lg active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          >
            {isSubmitting ? (
              <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              t('inventory.roastedBulk.adjust.submit')
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
